// AB module: visual field mapping editor (Wave 2 "UI parity", Agent FE-3).
// Prefix ab.map. — key-for-key parity with abMap.de.js is mandatory (RULES).
const abMap = {
  // ---- Shell / entry ----
  'ab.map.title': 'Field mapping',
  'ab.map.subtitle': 'Define per field where the value comes from',
  'ab.map.adminOnlyTitle': 'Administrators only',
  'ab.map.adminOnlyHint': 'The visual field mapping editor is reserved for administrators (preview/save are protected server-side).',
  'ab.map.pickDocTitle': 'Choose a test document',
  'ab.map.pickDocHint': 'Choose a document to test the ruleset against.',
  'ab.map.pickDocPlaceholder': 'Choose a document…',
  'ab.map.pickDocConfirm': 'Continue',
  'ab.map.pickDocLoading': 'Loading documents…',
  'ab.map.pickDocEmpty': 'No documents available.',
  'ab.map.testDocLabel': 'Test document',
  'ab.map.switchDoc': 'Switch test document',
  'ab.map.existingLoadedMsg': 'Existing ruleset "{supplier}" v{version} loaded — saving creates a new version.',
  'ab.map.newRulesetHint': 'No ruleset stored for this document — empty form.',
  'ab.map.unsupportedBanner': 'This ruleset uses advanced features the visual editor cannot represent. Please edit it in the JSON tab (expert mode).',
  'ab.map.loadError': 'Could not load the ruleset/document',

  // ---- Tabs ----
  'ab.map.tab.supplier': 'Supplier',
  'ab.map.tab.positions': 'Line items',
  'ab.map.tab.fields': 'Fields',
  'ab.map.tab.json': 'JSON',

  // ---- Supplier tab ----
  'ab.map.supplierIdLabel': 'Supplier ID',
  'ab.map.supplierIdPlaceholder': 'e.g. xometry',
  'ab.map.supplierNameLabel': 'Name',
  'ab.map.supplierNamePlaceholder': 'Display name',
  'ab.map.detectionLabel': 'Detection',
  'ab.map.detectionHint': 'Unique, stable text snippets that appear in every order confirmation of this supplier (e.g. domain, fixed heading).',
  'ab.map.detectionPlaceholder': 'Text snippet + Enter',
  'ab.map.unsupportedIdentityHint': 'This ruleset is edited in the JSON tab (expert mode); the supplier fields here are informational only.',

  // ---- Line items tab ----
  'ab.map.strategyLabel': 'Strategy',
  'ab.map.strategy.column_rows': 'Columns (coordinates — mark in the PDF)',
  'ab.map.strategy.blocks': 'Line blocks (text pattern)',
  'ab.map.strategyHintColumns': 'Line items are table rows; fields come from x-columns (mark in the PDF).',
  'ab.map.strategyHintBlocks': 'Each line item is a text block (detected via a start pattern); fields are read via regex from the block text.',
  'ab.map.tableTitle': 'Line item table',
  'ab.map.rowAnchorPick': 'Choose line-item column (anchor)',
  'ab.map.rowAnchorHint': 'the column with the line item numbers',
  'ab.map.rowAnchorPatternLabel': 'Anchor pattern',
  'ab.map.allPagesLabel': 'Table spans multiple pages (process all pages)',
  'ab.map.rowEndLabel': 'Table end',
  'ab.map.rowEndNone': 'none (until end of page)',
  'ab.map.rowEndPick': 'Choose word',
  'ab.map.rowEndRemove': 'remove',
  'ab.map.rowEndHint': 'Optional. Choose only a word below all values (e.g. "net amount"). Never the currency/"price".',
  'ab.map.bandPadLabel': 'Band padding (px)',
  'ab.map.pageLabel': 'Page',
  'ab.map.blocksTitle': 'Line blocks',
  'ab.map.startPatternLabel': 'Start pattern (block start, group 1 = line number)',
  'ab.map.requirePatternLabel': 'Condition per block (optional)',
  'ab.map.requirePatternPlaceholder': 'e.g. Customer PO no.:',
  'ab.map.blocksHint': 'A block runs from one start pattern to the next. The "condition" must appear in the block, otherwise it is skipped.',
  'ab.map.deliveryTitle': 'Delivery (date + quantity)',
  'ab.map.deliveryHint': 'EEIND and MENGE are read paired from the date/quantity list below the anchor. Set the capture location of EEIND and MENGE to "from delivery" in the "Fields" tab.',
  'ab.map.deliveryAnchorLabel': 'Anchor',
  'ab.map.deliveryAnchorPlaceholder': 'e.g. requested delivery date',
  'ab.map.deliveryPairIndexLabel': 'Pair index (which delivery row)',
  'ab.map.deliveryAdvanced': 'Advanced (override patterns)',
  'ab.map.deliveryDatePatternLabel': 'Date pattern',
  'ab.map.deliveryNumPatternLabel': 'Quantity pattern',
  'ab.map.deliveryStopPrefixLabel': 'Stop prefix',
  'ab.map.patternDefaultPlaceholder': '(engine default)',
  'ab.map.rowSkipTitle': 'Skip delivered line items',
  'ab.map.rowSkipSubtitle': 'optional (row_skip)',
  'ab.map.rowSkipPlaceholder': 'Marker regex + Enter (e.g. delivered)',
  'ab.map.rowSkipHint': 'Line items whose row/block text contains one of these regex markers are NOT extracted.',
  'ab.map.yearPatternLabel': 'Year source (pattern)',

  // ---- Fields tab ----
  'ab.map.fieldsTitle': 'Fields',
  'ab.map.fieldsHint': 'Expand a field card to edit — the source is highlighted in the PDF on the left.',
  'ab.map.scopeLabel': 'Capture location',
  'ab.map.scope.off': 'not captured',
  'ab.map.scope.header': 'Header (whole document)',
  'ab.map.scope.lineColumn': 'Line item column',
  'ab.map.scope.lineBlock': 'Line item block (text pattern)',
  'ab.map.scope.delivery': 'from delivery (anchor)',
  'ab.map.headerMethodLabel': 'Capture method',
  'ab.map.headerMethod.box': 'Mark area on PDF',
  'ab.map.headerMethod.regex': 'Via text pattern',
  'ab.map.headerMethod.presence': 'Fixed value',
  'ab.map.headerBoxPick': 'Mark area (click a word)',
  'ab.map.headerBoxValue': 'Page {page}, x {x0}–{x1}',
  'ab.map.headerRegexLabel': 'Pattern',
  'ab.map.headerRegexPlaceholder': 'e.g. No\\.\\s*(\\d+)',
  'ab.map.headerGroupLabel': 'Group',
  'ab.map.headerPresenceLabel': 'Value if present',
  'ab.map.headerPresencePlaceholder': 'EUR',
  'ab.map.lineSourceLabel': 'Source',
  'ab.map.lineSource.extract': 'Read from the PDF (column/text)',
  'ab.map.lineSource.sequence': 'Running number (10/20/30 …)',
  'ab.map.sequenceHint': 'Line number is counted (document has no own line number).',
  'ab.map.sequenceStartLabel': 'Start',
  'ab.map.sequenceStepLabel': 'Step',
  'ab.map.formatLabel': 'Format',
  'ab.map.columnPick': 'Choose column (click a word)',
  'ab.map.columnNone': 'no column yet',
  'ab.map.columnValue': 'x {min}–{max}',
  'ab.map.unitPick': 'Choose unit column (click a word)',
  'ab.map.decimalsLabel': 'Decimal places',
  'ab.map.blockPatternLabel': 'Pattern',
  'ab.map.blockPatternPlaceholder': 'Label:\\s*(\\S+)',
  'ab.map.blockGroupLabel': 'Group',
  'ab.map.blockPpuPatternLabel': 'Pattern (price + unit)',
  'ab.map.blockPpuPriceGroupLabel': 'Price group',
  'ab.map.blockPpuUnitGroupLabel': 'Unit group',
  'ab.map.blockPpuCodeGroupLabel': 'Code group',
  'ab.map.patternPickHint': 'Clicking a word in the PDF applies its text as a pattern suggestion (escaped); further clicks append.',
  'ab.map.recognizedValue': '✓ {value}',
  'ab.map.recognizedValueMore': '✓ {value} (+{n})',
  'ab.map.recognizedEmpty': 'empty',
  'ab.map.activePickChip': 'Active: {target} — now click in the PDF on the left.',
  'ab.map.activePickCancel': 'Cancel',
  'ab.map.pickTarget.rowAnchor': 'Line item column (anchor)',
  'ab.map.pickTarget.rowEnd': 'Table end word',
  'ab.map.pickTarget.headerBox': '{field} – header area',
  'ab.map.pickTarget.headerRegex': '{field} – text pattern',
  'ab.map.pickTarget.headerPresence': '{field} – fixed value',
  'ab.map.pickTarget.column': '{field} – column',
  'ab.map.pickTarget.unit': '{field} – unit column',
  'ab.map.pickTarget.pattern': '{field} – text pattern',

  // ---- describeField() (rulesetModel.js) ----
  'ab.map.describe.off': 'not captured',
  'ab.map.describe.delivery': 'from delivery anchor (date + quantity, paired)',
  'ab.map.describe.headerBox': 'Header: area marked on page {page}',
  'ab.map.describe.headerBoxMissing': 'Header: area not marked yet',
  'ab.map.describe.headerPresence': 'Header: fixed value "{value}", if present',
  'ab.map.describe.headerRegex': 'Header: via text pattern',
  'ab.map.describe.headerRegexMissing': 'Header: text pattern missing',
  'ab.map.describe.sequence': 'running number {start}, +{step} per line item',
  'ab.map.describe.blockPpu': 'Block: price ÷ unit (text pattern)',
  'ab.map.describe.blockPpuMissing': 'Block: price/unit pattern missing',
  'ab.map.describe.columnPpu': 'Line item column: price ÷ unit',
  'ab.map.describe.columnPpuMissing': 'Line item column: choose price/unit column',
  'ab.map.describe.blockPeCode': 'Block: price per unit code (÷10^code)',
  'ab.map.describe.blockPeCodeMissing': 'Block: price/code pattern missing',
  'ab.map.describe.blockLabel': 'Block: text pattern (group {group}), {format}',
  'ab.map.describe.blockLabelMissing': 'Block: text pattern missing',
  'ab.map.describe.column': 'Line item column: column x {min}–{max}, {format}',
  'ab.map.describe.columnMissing': 'Line item column: choose column, {format}',

  // ---- Formats (dropdown labels) ----
  'ab.map.format.text': 'Text',
  'ab.map.format.decimal_de': 'Number (German: 1,234.56 → 1.234,56)',
  'ab.map.format.decimal_us': 'Number (English: 1,234.56)',
  'ab.map.format.date_dmy': 'Date DD.MM.YYYY',
  'ab.map.format.date_isoweek_monday': 'Calendar week KW/YY in text',
  'ab.map.format.date_mon_dmy': 'Date DD-Mon-YY (English/German month)',
  'ab.map.format.isoweek_week_column': 'Calendar week (column) → Monday',
  'ab.map.format.price_per_unit': 'Price per unit',
  'ab.map.format.price_per_pe_code': 'Price per unit code (÷10^code)',
  'ab.map.format.clean': 'Cleaned text (soft hyphen/nbsp)',

  // ---- JSON tab ----
  'ab.map.jsonExpertTitle': 'Expert mode (JSON)',
  'ab.map.jsonReadOnlyHint': 'Read-only — generated from the form.',
  'ab.map.jsonUnsupportedHint': 'This ruleset can only be edited here (source of truth).',
  'ab.map.jsonInvalid': 'Invalid JSON: {error}',

  // ---- Actions ----
  'ab.map.aiSuggestButton': 'AI suggestion',
  'ab.map.aiSuggestRunning': 'AI is thinking…',
  'ab.map.aiSuggestStarting': 'Starting AI suggestion…',
  'ab.map.aiSuggestAdminOnly': 'AI suggestions are reserved for administrators.',
  'ab.map.aiSuggestNotConfigured': 'AI suggestion is not configured (LLM unavailable).',
  'ab.map.aiSuggestAlreadyRunning': 'An AI suggestion job is already running for this document.',
  'ab.map.aiSuggestTimeout': 'AI suggestion exceeded the time limit.',
  'ab.map.aiSuggestFailed': 'AI suggestion failed',
  'ab.map.aiSuggestBanner': 'AI suggestion ({model}): {lineCount} line item(s), recognized: {supplier}. Please review the fields, then "Save & activate" — or "Discard AI suggestion".',
  'ab.map.aiSuggestBannerNoMatch': 'AI suggestion ({model}): {lineCount} line item(s), no supplier recognized. Please review the fields, then "Save & activate" — or "Discard AI suggestion".',
  'ab.map.aiSuggestWarnings': 'Notes: {warnings}',
  'ab.map.aiSuggestDiscard': 'Discard AI suggestion',
  'ab.map.aiSuggestDiscardedMsg': 'AI suggestion discarded.',
  'ab.map.previewButton': 'Preview',
  'ab.map.previewRunning': 'Preview running…',
  'ab.map.previewResultTitle': 'Preview — {lineCount} line(s), recognized: {supplier}',
  'ab.map.previewMatchedNo': 'no',
  'ab.map.previewPosHeader': 'Pos',
  'ab.map.previewEmptyHint': 'No preview yet — choose a test document and click "Preview".',
  'ab.map.previewError': 'Preview failed',
  'ab.map.saveActivateButton': 'Save & activate',
  'ab.map.saveActivateRunning': 'Saving & processing…',
  'ab.map.saveDraftButton': 'Save as draft',
  'ab.map.saveDraftRunning': 'Saving…',
  'ab.map.saveSupplierIdRequired': 'Please provide a supplier ID.',
  'ab.map.saveActivatedMsg': 'Saved as v{version}, activated and document reprocessed → {status} ({lineCount} line item(s)).',
  'ab.map.saveActivatedMsgNoDoc': 'Saved as v{version} and activated.',
  'ab.map.saveDraftMsg': 'Saved as draft (v{version}) — not yet active.',
  'ab.map.reprocessFailedHint': 'Ruleset saved, reprocessing the test document failed: {error}',

  // ---- Entry point from ruleset administration ----
  'ab.map.openEditorButton': 'Edit in mapping editor',
};
export default abMap;
